import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Box,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Paper,
  Typography,
  Alert,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import FactoryIcon from '@mui/icons-material/Factory';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import { styled } from '@mui/material/styles';
import { useFormik } from 'formik';
import * as yup from 'yup';
import EmpSelectbox from '../../../components/common/form/autocomplete/EmpSelectbox';
import PartnerSelectbox from '../../../components/common/form/autocomplete/PartnerSelectbox';
import DesignSelectModal from '../../../components/common/form/modal/DesignSelectModal';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
  },
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#f5f5f5',
  borderRadius: theme.shape.borderRadius,
}));

const validationSchema = yup.object({
  processType: yup.string().required('생산 종류를 선택해주세요'),
  expectedEndDate: yup.string().required('예정 종료일을 선택해주세요'),
  workerUuid: yup.string().when('type', {
    is: '내부',
    then: (schema) => schema.required('작업자를 선택해주세요'),
    otherwise: (schema) => schema.notRequired(),
  }),
  outsourcePartnerUuid: yup.string().when('type', {
    is: '외주',
    then: (schema) => schema.required('외주업체를 선택해주세요'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

// 생산 종류 - 워크플로우 공정에서 동적으로 가져옴

/**
 * 생산 시작 다이얼로그
 * - planId가 전달되면 생산계획 선택 없이 바로 설계 선택부터 시작
 * - 제품 정보를 명확히 표시
 */
const ProductionStartDialog = ({ 
  open, 
  onClose, 
  onSave, 
  processType,
  planId = null,          // 생산계획 ID (자동 설정)
  planNumber = null,      // 생산계획 번호
  productName = null,     // 제품명
  productCode = null,     // 제품코드
  cavity = null,          // Cavity
  quantity = null,        // 수량
  planData = null,        // 전체 계획 데이터 (추가 정보용)
  routingData = null,     // 워크플로우 라우팅 데이터 (공정 목록)
}) => {
  const [productionType, setProductionType] = useState('내부');
  const [openDesignModal, setOpenDesignModal] = useState(false);
  
  // 현재 로그인한 사용자 정보
  const [currentUser, setCurrentUser] = useState({
    userId: '',
    userName: '',
  });
  
  // 사용자 정보 불러오기
  useEffect(() => {
    const userInfo = localStorage.getItem('user');
    
    if (userInfo) {
      try {
        const user = JSON.parse(userInfo);
        setCurrentUser({
          userId: user.usrUuid || '',
          userName: user.usrNm || '사용자',
        });
      } catch (e) {
        console.error('사용자 정보 파싱 에러:', e);
      }
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      // 생산 종류
      processType: '',
      processTypeLabel: '',
      
      // 설계 연결 (선택사항)
      designId: '',
      designManuNo: '',
      designProducts: [],
      
      type: '내부',
      workerUuid: '',
      worker: '',
      expectedEndDate: '',
      outsourcePartnerUuid: '',
      outsourcePartnerName: '',
      outsourceContact: '',
      remarks: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const saveData = {
        ...values,
        planId: planId,
        productionType: values.type === '내부' ? 'INTERNAL' : 'EXTERNAL',
      };
      onSave(saveData);
      handleClose();
    },
  });

  // 다이얼로그 열릴 때 초기화
  useEffect(() => {
    if (open) {
      formik.resetForm();
      setProductionType('내부');
      
      // 기본 담당자 설정 (우선순위: 생산계획 담당자 > 현재 로그인 사용자)
      const defaultWorkerUuid = planData?.productionManagerUuid || currentUser.userId;
      const defaultWorkerName = planData?.productionManagerName || currentUser.userName;
      
      if (defaultWorkerUuid) {
        setTimeout(() => {
          formik.setFieldValue('workerUuid', defaultWorkerUuid);
          formik.setFieldValue('worker', defaultWorkerName);
        }, 100);
      }
    }
  }, [open, planData, currentUser]);

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    setProductionType(newType);
    formik.setFieldValue('type', newType);
    
    // 타입 변경 시 관련 필드 초기화
    if (newType === '내부') {
      formik.setFieldValue('outsourcePartnerUuid', '');
      formik.setFieldValue('outsourcePartnerName', '');
      formik.setFieldValue('outsourceContact', '');
    } else {
      formik.setFieldValue('workerUuid', '');
      formik.setFieldValue('worker', '');
    }
  };

  // 설계 선택 핸들러
  const handleDesignSelect = (design) => {
    formik.setFieldValue('designId', design.designId || design.id);
    formik.setFieldValue('designManuNo', design.designNumber || design.manuNo);
    formik.setFieldValue('designProducts', design.products || []);
    setOpenDesignModal(false);
  };

  const handleClose = () => {
    setProductionType('내부');
    formik.resetForm();
    onClose();
  };

  // 계획 정보 표시용 데이터
  const displayPlanNumber = planNumber || planData?.planNumber || planData?.planNo || '-';
  const displayProductName = productName || planData?.productName || '-';
  const displayProductCode = productCode || planData?.productCode || '';
  const displayCavity = cavity || planData?.cavity || '-';
  const displayQuantity = quantity || planData?.quantity || planData?.planQuantity || '-';
  const displayProductionManager = planData?.productionManagerName || '-';

  return (
    <>
      <BootstrapDialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FactoryIcon color="primary" />
          생산 시작
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box component="form" onSubmit={formik.handleSubmit}>
            
            {/* ========================================== */}
            {/* 현재 생산계획 정보 (읽기전용) */}
            {/* ========================================== */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2.5, 
                mb: 3, 
                bgcolor: '#e8f5e9', 
                borderRadius: 2, 
                border: '2px solid #4caf50' 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InventoryIcon color="success" />
                <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
                  생산계획 정보
                </Typography>
                <Chip 
                  label="자동 설정" 
                  size="small" 
                  color="success" 
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    생산계획 번호
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.dark">
                    {displayPlanNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    제품명
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {displayProductName}
                    {displayProductCode && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({displayProductCode})
                      </Typography>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    계획 수량
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {displayQuantity}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    지정 생산담당자
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="warning.dark">
                    {displayProductionManager}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* ========================================== */}
            {/* 1단계: 생산 종류 선택 */}
            {/* ========================================== */}
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                mb: 3, 
                bgcolor: '#fff3e0', 
                borderRadius: 2, 
                border: '2px solid #ff9800' 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CategoryIcon color="warning" />
                <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">
                  1단계: 생산 종류 선택 *
                </Typography>
              </Box>
              
              {/* 워크플로우 공정 목록이 없는 경우 */}
              {(!routingData || !routingData.details || routingData.details.length === 0) ? (
                <Alert severity="warning">
                  워크플로우가 설정되지 않았습니다. 생산계획에서 워크플로우를 먼저 설정해주세요.
                </Alert>
              ) : (
                <FormControl 
                  fullWidth 
                  error={formik.touched.processType && Boolean(formik.errors.processType)}
                >
                  <InputLabel id="process-type-label">생산 종류 (공정 선택)</InputLabel>
                  <Select
                    labelId="process-type-label"
                    name="processType"
                    value={formik.values.processType}
                    onChange={(e) => {
                      const selected = routingData.details.find(p => p.routingDetUuid === e.target.value);
                      formik.setFieldValue('processType', e.target.value);
                      formik.setFieldValue('processTypeLabel', selected?.processNm || '');
                    }}
                    label="생산 종류 (공정 선택)"
                  >
                    {routingData.details
                      .filter(detail => detail.processNm !== '설계' && detail.processNm !== '생산완료') // 설계/생산완료 제외
                      .map((detail) => {
                        const isCompleted = detail.processStatus === 'COMPLETED';
                        const isInProgress = detail.processStatus === 'IN_PROGRESS';
                        
                        return (
                          <MenuItem
                            key={detail.routingDetUuid}
                            value={detail.routingDetUuid}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight="medium">
                                  {detail.processSeq}. {detail.processNm}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {isCompleted ? '✅ 완료' : isInProgress ? '🟡 진행중' : '⏳ 대기'}
                                </Typography>
                              </Box>
                              {isCompleted && (
                                <Chip label="완료" size="small" color="success" />
                              )}
                              {isInProgress && (
                                <Chip label="진행중" size="small" color="warning" />
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                  </Select>
                  {formik.touched.processType && formik.errors.processType && (
                    <FormHelperText>{formik.errors.processType}</FormHelperText>
                  )}
                </FormControl>
              )}
            </Paper>

            {/* ========================================== */}
            {/* 2단계: 설계 선택 (선택사항) */}
            {/* ========================================== */}
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                mb: 3, 
                bgcolor: '#e3f2fd', 
                borderRadius: 2, 
                border: '2px solid #2196f3' 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DesignServicesIcon color="primary" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary.dark">
                  2단계: 설계 선택 (선택사항)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  설계가 있으면 선택하세요
                </Typography>
              </Box>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={9}>
                  {formik.values.designId ? (
                    <Paper elevation={1} sx={{ p: 2, bgcolor: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            선택된 설계
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="primary">
                            {formik.values.designManuNo}
                          </Typography>
                        </Box>
                        <Chip label="선택됨" size="small" color="primary" />
                      </Box>
                      
                      {/* 설계에 포함된 제품 목록 */}
                      {formik.values.designProducts.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            설계 제품 목록
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell>제품명</TableCell>
                                  <TableCell>규격</TableCell>
                                  <TableCell align="center">수량</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {formik.values.designProducts.map((product, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{product.productName}</TableCell>
                                    <TableCell>{product.spec || '-'}</TableCell>
                                    <TableCell align="center">{product.quantity || 1}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Paper>
                  ) : (
                    <Alert severity="info" icon={<DesignServicesIcon />}>
                      설계가 필요하면 선택해주세요 (선택사항)
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => setOpenDesignModal(true)}
                    sx={{ height: '56px' }}
                  >
                    설계 선택
                  </Button>
                </Grid>
              </Grid>
              
              {formik.touched.designId && formik.errors.designId && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {formik.errors.designId}
                </Typography>
              )}
            </Paper>

            {/* ========================================== */}
            {/* 3단계: 생산 정보 입력 */}
            {/* ========================================== */}
            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 2 }}>
              3단계: 생산 정보 입력
            </Typography>
            
            <Grid container spacing={3}>
              {/* 생산 구분 */}
              <Grid item xs={12}>
                <FormLabel component="legend">생산 구분 *</FormLabel>
                <RadioGroup
                  row
                  value={productionType}
                  onChange={handleTypeChange}
                >
                  <FormControlLabel value="내부" control={<Radio />} label="내부 생산" />
                  <FormControlLabel value="외주" control={<Radio />} label="외주 생산" />
                </RadioGroup>
              </Grid>

              {/* 내부 생산 - 작업자 선택 */}
              {productionType === '내부' ? (
                <Grid item xs={12}>
                  <EmpSelectbox
                    key={`worker-${formik.values.workerUuid || 'new'}`}
                    formik={formik}
                    name="workerUuid"
                    nameField="worker"
                    label="담당자 / 작업자 *"
                    variant="outlined"
                    margin="none"
                    required
                  />
                </Grid>
              ) : (
                /* 외주 생산 - 외주업체 선택 */
                <>
                  <Grid item xs={12} sm={6}>
                    <PartnerSelectbox
                      formik={formik}
                      name="outsourcePartnerUuid"
                      label="외주업체"
                      variant="outlined"
                      margin="none"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="담당자 연락처"
                      fullWidth
                      name="outsourceContact"
                      value={formik.values.outsourceContact}
                      onChange={formik.handleChange}
                      placeholder="010-0000-0000"
                    />
                  </Grid>
                </>
              )}

              {/* 예정 종료일 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="예정 종료일"
                  type="date"
                  fullWidth
                  required
                  name="expectedEndDate"
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.expectedEndDate}
                  onChange={formik.handleChange}
                  error={formik.touched.expectedEndDate && Boolean(formik.errors.expectedEndDate)}
                  helperText={formik.touched.expectedEndDate && formik.errors.expectedEndDate}
                />
              </Grid>
              
              {/* 시작일 (자동: 오늘) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="시작일"
                  type="date"
                  fullWidth
                  disabled
                  value={new Date().toISOString().split('T')[0]}
                  InputLabelProps={{ shrink: true }}
                  helperText="자동 설정 (오늘)"
                />
              </Grid>

              {/* 비고 */}
              <Grid item xs={12}>
                <TextField
                  label="비고"
                  fullWidth
                  multiline
                  rows={2}
                  name="remarks"
                  value={formik.values.remarks}
                  onChange={formik.handleChange}
                  placeholder="생산 관련 특이사항을 입력하세요"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit">
            취소
          </Button>
          <Button
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={!formik.values.processType || formik.isSubmitting}
            startIcon={<FactoryIcon />}
          >
            생산 시작
          </Button>
        </DialogActions>
      </BootstrapDialog>

      {/* 설계 선택 모달 */}
      <DesignSelectModal
        open={openDesignModal}
        onClose={() => setOpenDesignModal(false)}
        onSelect={handleDesignSelect}
        planId={planId}
        statusFilter=""  // 빈 문자열로 모든 상태 허용 (TODO: 실제 운영시 APPROVED로 변경)
      />
    </>
  );
};

export default ProductionStartDialog;
